
import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useFeaturedTracks } from '@/hooks/useFeaturedTracks';
import AddTrackForm from './featured-tracks/AddTrackForm';
import EmptyTracksList from './featured-tracks/EmptyTracksList';
import TracksList from './featured-tracks/TracksList';
import TracksLoading from './featured-tracks/TracksLoading';
import FeaturedTracksHeader from './featured-tracks/FeaturedTracksHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

const FeaturedTracksManager: React.FC = () => {
  const { tracks, isLoading, error, refreshTracks, addFeaturedTrack, deleteTrack, reorderTracks } = useFeaturedTracks();
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const handleAddSuccess = () => {
    setShowUrlInput(false);
    refreshTracks();
  };

  const handleAddCancel = () => {
    setShowUrlInput(false);
  };
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshTracks();
    setIsRefreshing(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <FeaturedTracksHeader 
            isLoading={isLoading || isRefreshing}
            onRefresh={handleRefresh}
            onAddTrack={() => setShowUrlInput(true)}
          />
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {showUrlInput && (
            <AddTrackForm 
              onSuccess={handleAddSuccess}
              onCancel={handleAddCancel}
            />
          )}
          
          {isLoading ? (
            <TracksLoading />
          ) : (
            <>
              {tracks.length === 0 && !error ? (
                <EmptyTracksList />
              ) : (
                tracks.length > 0 && (
                  <TracksList 
                    tracks={tracks} 
                    onDelete={deleteTrack}
                    onReorder={reorderTracks}
                  />
                )
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeaturedTracksManager;
